import Cookies from 'js-cookie';

/*
 * Meta's own pixel (fbevents.js) writes these first-party cookies on our domain:
 * `_fbp` is the Facebook Browser ID (fb.<subdomain>.<createdAtMs>.<random>) and
 * `_fbc` is the Facebook Click ID (fb.<subdomain>.<createdAtMs>.<fbclid>).
 *
 * We only read them. Nothing here creates or refreshes a cookie: a self-minted
 * value carries no meaning for Meta, because they never observed it in a browser
 * context and have nothing to match it against.
 *
 * Both are forwarded to PostHog as regular event properties so the Meta
 * Conversions API destination can map them onto `user_data.fbp` / `user_data.fbc`.
 */

/**
 * Single source of truth for the Meta property contract, keyed by the PostHog
 * property name (deliberately identical to Meta's user_data keys). Everything
 * else (the exported name list, extraction, cleanup, reserved-key handling)
 * derives from this map, so adding or renaming a property is one edit.
 *
 * Patterns: Meta's documented formats are fb.<subdomainIndex>.<createdAtMs>.<payload>,
 * where the `_fbp` payload is a random number and the `_fbc` payload is an
 * fbclid (URL-safe: letters, digits, `_`, `-`). The fbc charset mirrors the
 * regex in PostHog's Meta Ads destination, which drops anything looser
 * (decoded `%`, spaces, `=`), because Meta can reject such values with
 * subcode 2804001. A malformed value is worse than none: Meta rejects the
 * whole user_data block rather than just the offending field.
 *
 * The length bounds match the server-side reader in
 * api/services/tracking/migrationContextService.js (change-together pair):
 * these values also ride the X-Tracking-Context header and persisted
 * analytics payloads, so an unbounded cookie value must not pass.
 */
const META_COOKIE_DESCRIPTORS = {
	      'fbp': {'cookieName': '_fbp', 'pattern': /^fb(?:\.\d{1,20}){3}$/},
	      'fbc': {'cookieName': '_fbc', 'pattern': /^fb(?:\.\d{1,20}){2}\.[\w-]{1,512}$/}
      },

      META_PROPERTY_NAMES = Object.freeze(Object.keys(META_COOKIE_DESCRIPTORS)),

      /**
       * Only the keys that currently have a valid cookie. Callers use the
       * absence of a key to mean "stop sending this", which is what makes a
       * withdrawn consent propagate instead of leaving a stale value behind.
       * @returns {{fbp?: string, fbc?: string}}
       */
      getMetaCookieProperties = () => Object.entries(META_COOKIE_DESCRIPTORS).reduce(
	      (properties, [propertyName, {cookieName, pattern}]) => {
		      const value = Cookies.get(cookieName);

		      if (pattern.test(value ?? '')) {
			      properties[propertyName] = value;
		      }

		      return properties;
	      },
	      {}
      );

export {
	META_PROPERTY_NAMES,
	getMetaCookieProperties
};